// src/components/SingleProviderScreenPanel.jsx
// Simplified vendor-screening panel for a single, already-known provider —
// no list picker, since the scope is fixed to one NPI rather than a
// selection of tracked-provider lists. Used from the physician pages
// (PhysicianPage, PhysicianOwnersPage, PhysicianResearchPage) to screen a
// company this specific provider already has a relationship with.
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function SingleProviderScreenPanel({ companyId, companyName, npi, providerName, userId, fetchFn, onCancel }) {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);

  async function handleGenerate() {
    if (generating) return;
    // Must open synchronously, inside the click handler — opening a tab
    // after the await below would be blocked as a popup by most browsers.
    const reportWindow = window.open("", "_blank");
    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Generating report… — ProviderPayWatch</title>
            <style>
              body { background:#0a1628; color:#e2e8f0; font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; margin:0; }
              .box { text-align:center; }
              .spinner { width:32px; height:32px; border:3px solid rgba(245,158,11,0.2); border-top-color:#f59e0b; border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto 16px; }
              @keyframes spin { to { transform: rotate(360deg); } }
              p { color:#94a3b8; font-size:14px; }
            </style>
          </head>
          <body>
            <div class="box">
              <div class="spinner"></div>
              <p>Screening ${companyName.replace(/</g, "&lt;")}…<br>This can take a moment.</p>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
    }
    setGenerating(true);
    setGenError(null);
    try {
      const res = await fetchFn("/api/generate-conflict-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          reportType: "vendor_scan",
          companyId,
          trackedNpis: [npi],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === "failed") {
        setGenError(data.error || "Report generation failed. Please try again.");
        if (reportWindow) reportWindow.close();
        return;
      }
      if (reportWindow) {
        reportWindow.location.href = `/conflict-report/${data.reportId}`;
      } else {
        navigate(`/conflict-report/${data.reportId}`);
      }
      onCancel();
    } catch {
      setGenError("Network error. Please try again.");
      if (reportWindow) reportWindow.close();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="bg-navy-900 border border-red-500/20 rounded-lg p-3 mt-3 space-y-2.5">
      <p className="text-white/70 text-xs font-medium">
        Screen {companyName} for {providerName || `NPI ${npi}`}
      </p>
      <p className="text-white/30 text-xs">
        Checks this provider's own relationship with {companyName} — exclusion, dual/triple interest, POD risk —
        plus market-wide signals for the company as a whole.
      </p>
      {genError && <p className="text-red-400 text-xs">{genError}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-all"
        >
          {generating ? "⏳ Generating…" : "🛡 Screen this company"}
        </button>
        <button onClick={onCancel} className="text-white/30 hover:text-white/60 text-xs">Cancel</button>
      </div>
    </div>
  );
}
